import { AbstractStore } from "@yuigoto/abst";

/**
 * core/glambox/GlamboxCookieStore
 * ----------------------------------------------------------------------
 * Loja de Cookies da Glambox! 🍪 (desculpa).
 *
 * Classe helper para gerenciar cookies em um domínio/caminho, atua como um
 * singleton.
 *
 * @since 0.0.1
 */
export class GlamboxCookieStore extends AbstractStore {
  /**
   * Instância do handler, para garantir que será um singleton.
   *
   * @type {GlamboxCookieStore}
   */
  static __;

  // --------------------------------------------------------------------

  /**
   * Prefixo usado p/ namespacing do cookie.
   *
   * @type {String}
   */
  _prefix = null;

  /**
   * Caminho, dentro do domínio atual, aonde cookie é considerado válido.
   *
   * @type {String}
   */
  _path = null;

  /**
   * Domínios ao qual este cookie está atrelado.
   *
   * @type {String}
   */
  _domain = null;

  /**
   * Se atributos do cookie devem ser enviados via HTTPS.
   *
   * @type {Boolean}
   */
  _secure = false;

  /**
   * Data de expiração, formato RFC.
   *
   * @type {String}
   */
  _expires = null;

  // Lifecycle
  // --------------------------------------------------------------------

  /**
   * @param {String} path
   *     Caminho dentro domínio aonde o cookie será válido
   * @param {String} domain
   *     Domínio ao qual o cookie será vinculado
   * @param {Boolean} secure
   *     Se disponível apenas via HTTPS
   * @param {Date} expires
   *     Data de expiração do cookie
   * @param {String} prefix
   *     Prefixo para namespacing
   */
  constructor (
    path = null,
    domain = null,
    secure = false,
    expires = null,
    prefix = ""
  ) {
    super();

    if (GlamboxCookieStore.__ !== null && GlamboxCookieStore.__ !== undefined) {
      GlamboxCookieStore.__.path = path || null;
      GlamboxCookieStore.__.domain = domain || null;
      GlamboxCookieStore.__.secure = (secure === true);
      GlamboxCookieStore.__.expires = (expires && expires instanceof Date)
        ? expires.toUTCString()
        : "";

      if (typeof prefix === "string" && prefix.trim() !== "") {
        GlamboxCookieStore.__.prefix = prefix.trim();
      }

      return GlamboxCookieStore.__;
    }

    this.path = path || null;
    this.domain = domain || null;
    this.secure = (secure === true);
    this.expires = (expires && expires instanceof Date)
      ? expires.toUTCString()
      : "";

    if (typeof prefix === "string" && prefix.trim() !== "") {
      this.prefix = prefix.trim();
    }

    GlamboxCookieStore.__ = this;
  }

  // Getters + Setters
  // --------------------------------------------------------------------

  /**
   * Prefixo usado p/ namespacing do cookie.
   *
   * @type {String}
   */
  get prefix () {
    return this._prefix;
  }

  set prefix (value) {
    this._prefix = value;
  }

  /**
   * Caminho, dentro do domínio atual, aonde cookie é considerado válido.
   *
   * @type {String}
   */
  get path () {
    return this._path;
  }

  set path (value) {
    this._path = value;
  }

  /**
   * Domínios ao qual este cookie está atrelado.
   *
   * @type {String}
   */
  get domain () {
    return this._domain;
  }

  set domain (value) {
    this._domain = value;
  }

  /**
   * Se atributos do cookie devem ser enviados via HTTPS.
   *
   * @type {Boolean}
   */
  get secure () {
    return this._secure;
  }

  set secure (value) {
    this._secure = value;
  }

  /**
   * Data de expiração, formato RFC.
   *
   * @type {String}
   */
  get expires () {
    return this._expires;
  }

  set expires (value) {
    this._expires = value;
  }

  // --------------------------------------------------------------------

  /**
   * Retorna um item do cookie.
   *
   * @param {String} key
   *     Nome da chave desejada
   * @returns {*}
   */
  get (key) {
    if (this.prefix !== null && this.prefix !== undefined) {
      key = this.prefix + "_" + key.trim();
    }

    let cookie = document.cookie.split(";"),
      _rgx = new RegExp(`^${key}`);

    for (let i = 0; i < cookie.length; i++) {
      let pair = cookie[i].split(/=(.*)/);

      if (_rgx.test(pair[0].trim())) {
        return pair[1].trim();
      }
    }

    return null;
  }

  /**
   * Retorna todos os itens do domínio.
   *
   * @returns {Object}
   */
  getAll () {
    if (document.cookie === undefined || document.cookie === "") return {};

    let cookie = document.cookie.split(";"),
      length = cookie.length,
      returnable = {};

    if (this.prefix !== null && this.prefix !== undefined) {
      let _rgx = new RegExp(`^${this.prefix}_`);

      for (let n = 0; n < length; n++) {
        let pair = cookie[n].trim().split(/=(.*)/);

        if (_rgx.test(pair[0])) {
          returnable[pair[0]] = (typeof pair[1] === "string")
            ? pair[1].trim()
            : pair[1];
        }
      }
    } else {
      for (let n = 0; n < length; n++) {
        let pair = cookie[n].trim().split(/=(.*)/);

        returnable[pair[0]] = (typeof pair[1] === "string")
          ? pair[1].trim()
          : pair[1];
      }
    }

    return returnable;
  }

  /**
   * Define um item para o cookie.
   *
   * @param {String} key
   *     Chave a associar ao cookie
   * @param {*} value
   *     Valor da chave
   */
  set (key, value) {
    if (this.prefix !== null && this.prefix !== undefined) {
      key = this.prefix + "_" + key.trim();
    }

    document.cookie = `${key}=${value}` + this._param();
  }

  /**
   * Exclui uma entrada dos cookies.
   *
   * @param {String} key
   *     Nome da chave do cookie
   */
  remove (key) {
    if (this.prefix !== null && this.prefix !== undefined) {
      key = this.prefix + "_" + key.trim();
    }

    let cookie = document.cookie.split(";"),
      _rgx = new RegExp(`^${key}`);

    for (let i = 0; i < cookie.length; i++) {
      let pair = cookie[i].split(/=(.*)/);

      if (_rgx.test(pair[0].trim())) {
        document.cookie = cookie[i].trim() + this._param(true);
        break;
      }
    }
  }

  /**
   * Elimina todos os cookies relativos ao domínio.
   */
  clear () {
    let cookie = document.cookie.split(";");

    if (this.prefix !== null && this.prefix !== undefined) {
      let _rgx = new RegExp(`${this.prefix}_`);

      cookie.forEach(item => {
        let pair = item.trim().split(/=(.*)/);

        if (_rgx.test(pair[0])) {
          document.cookie = item.trim() + this._param(true);
        }
      });
    } else {
      cookie.forEach(item => {
        document.cookie = item.trim() + this._param(true);
      });
    }
  }

  // --------------------------------------------------------------------

  /**
   * Define valores para concatenar ao cookie, relativos aos parâmetros usados
   * ao instanciar a classe.
   *
   * @param {Boolean} toExpire
   *     Define se estamos usando o concatenador para expirar um cookie ou não
   * @returns {String}
   */
  _param (toExpire = false) {
    let returnable = [];

    if (this.path !== null && this.path !== undefined) {
      returnable.push(`path=${this.path}`);
    }

    if (this.domain !== null && this.domain !== undefined) {
      returnable.push(`domain=${this.domain}`);
    }

    if (this.secure === true) returnable.push("secure");

    if (toExpire === true) {
      returnable.push(`expires=Thu, 01 Jan 1970 00:00:00 UTC`);
    } else {
      if (this.expires !== null && this.expires !== undefined) {
        returnable.push(`expires=${this.expires}`);
      }
    }

    if (returnable.length > 0) return ";" + returnable.join(";");

    return "";
  }
}
